import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";

import api from "../../services/api";
import { Toast } from "primereact/toast";
import Menu from "../../components/Menu";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";

const initialState = {
  idtecdefeitotipo: null,
  nome: "",
  qualidade: "",
  meta_producao: "",
  meta_residuo: "",
};

function toNumberOrNull(value) {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export default function MetasTextil() {
  const [data, setData] = useState([]);
  const [form, setForm] = useState({ ...initialState });

  const [isEditing, setIsEditing] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);

  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const toast = useRef(null);

  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    []
  );

  const formatNumber = (value) => {
    const n = toNumberOrNull(value);
    if (n === null) return "-";
    return numberFormatter.format(n);
  };

  const showToast = (severity, summary, detail) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 2500,
    });
  };

  const hideDialog = () => {
    setIsDialogVisible(false);
    setIsEditing(false);
    setForm({ ...initialState });
  };

  const openEditDialog = (rowData) => {
    setForm({
      idtecdefeitotipo: rowData.idtecdefeitotipo ?? null,
      nome: rowData.nome ?? "",
      qualidade:
        rowData.qualidade === null || rowData.qualidade === undefined
          ? ""
          : String(rowData.qualidade),
      meta_producao:
        rowData.meta_producao === null || rowData.meta_producao === undefined
          ? ""
          : String(rowData.meta_producao),
      meta_residuo:
        rowData.meta_residuo === null || rowData.meta_residuo === undefined
          ? ""
          : String(rowData.meta_residuo),
    });

    setIsEditing(true);
    setIsDialogVisible(true);
  };

  const fetchData = useCallback(() => {
    setIsFetching(true);

    api
      .get("cadastros/metas-textil")
      .then((response) => {
        setData(response.data?.data ?? []);
      })
      .catch((error) => {
        console.log(error);
        showToast("error", "Erro", "Não foi possível carregar os registros");
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = () => {
    const payload = {
      nome: form.nome?.trim(),
      qualidade: toNumberOrNull(form.qualidade),
      meta_producao: toNumberOrNull(form.meta_producao),
      meta_residuo: toNumberOrNull(form.meta_residuo),
    };

    if (isEditing) {
      if (!form.idtecdefeitotipo) {
        showToast("warn", "Atenção", "Registro inválido para edição");
        return;
      }

      setIsSaving(true);

      api
        .post(`cadastros/metas-textil/update/${form.idtecdefeitotipo}`, payload)
        .then((response) => {
          showToast("success", "OK", response.data?.message ?? "Atualizado");
          hideDialog();
          fetchData();
        })
        .catch((error) => {
          console.log(error);
          const message =
            error?.response?.data?.message ?? "Não foi possível atualizar";
          showToast("error", "Erro", message);
        })
        .finally(() => {
          setIsSaving(false);
        });

      return;
    }

    setIsSaving(true);

    api
      .post("cadastros/metas-textil/store", payload)
      .then((response) => {
        showToast("success", "OK", response.data?.message ?? "Criado");
        hideDialog();
        fetchData();
      })
      .catch((error) => {
        console.log(error);
        const message =
          error?.response?.data?.message ?? "Não foi possível cadastrar";
        showToast("error", "Erro", message);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <div>
      <Menu />
      <Toast ref={toast} />

      <Dialog
        onHide={hideDialog}
        breakpoints={{ "960px": "90vw" }}
        style={{ minWidth: "30vw" }}
        visible={isDialogVisible}
        header={isEditing ? "Editar" : "Cadastrar"}
        footer={() => (
          <div>
            <Button
              className="p-button-secondary"
              label="Cancelar"
              onClick={hideDialog}
              disabled={isSaving}
            />
            <Button
              className="p-button-success"
              label="Salvar"
              loading={isSaving}
              onClick={handleSubmit}
            />
          </div>
        )}
      >
        <div className="p-fluid">
          <div className="p-field mt-2">
            <InputText
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              placeholder="Digite o nome do setor"
              readOnly={isEditing}
              type="text"
              value={form.nome}
            />
          </div>

          <div className="p-field">
            <InputText
              onChange={(e) => setForm({ ...form, qualidade: e.target.value })}
              placeholder="Meta Qualidade"
              type="number"
              value={form.qualidade}
            />
          </div>

          <div className="p-field">
            <InputText
              onChange={(e) =>
                setForm({ ...form, meta_producao: e.target.value })
              }
              placeholder="Meta Produção"
              type="number"
              value={form.meta_producao}
            />
          </div>

          <div className="p-field">
            <InputText
              onChange={(e) =>
                setForm({ ...form, meta_residuo: e.target.value })
              }
              placeholder="Meta Resíduo"
              type="number"
              value={form.meta_residuo}
            />
          </div>
        </div>
      </Dialog>

      <Card
        header={() => (
          <div
            className="p-d-flex p-flex-row p-jc-between p-ai-start"
            style={{ width: "100%" }}
          >
            <h1 className="p-m-3">Metas</h1>
          </div>
        )}
        className="p-m-3"
      >
        <DataTable
          emptyMessage="Nenhum registro encontrado."
          responsiveLayout="scroll"
          size="small"
          value={data}
          loading={isFetching}
        >
          <Column
            header="ID"
            field="idtecdefeitotipo"
            style={{ width: "15%" }}
          />
          <Column header="Setor" field="nome" />

          <Column
            header="Meta Qualidade"
            field="qualidade"
            body={(row) => formatNumber(row.qualidade)}
          />
          <Column
            header="Meta Produção"
            field="meta_producao"
            body={(row) => formatNumber(row.meta_producao)}
          />
          <Column
            header="Meta Resíduo"
            field="meta_residuo"
            body={(row) => formatNumber(row.meta_residuo)}
          />

          <Column
            header="Ações"
            style={{ width: "15%" }}
            body={(rowData) => (
              <div>
                <Button
                  className="p-button-primary p-mr-1"
                  icon="pi pi-pencil"
                  onClick={() => openEditDialog(rowData)}
                />
              </div>
            )}
          />
        </DataTable>
      </Card>
    </div>
  );
}
